import { Position as _Position } from '@interfaces/client-account/client-account-models.interface';
export interface Position extends _Position {}
import { PermissionType } from './permission-types';
import { RoleTypes } from './role-types';
export interface AggregatedAccounts {
  vgClientPoid: string;
  accounts: Account[];
}

export interface IClientAccounts extends AggregatedAccounts {
  serviceAgreements: ServiceAgreement[];
}

export interface Account {
  accountId: string;
  accountName: string;
  accountType: AANAccountType;
  balance: string;
  balanceAsOfDateTime: string;
  managed: boolean;
  productType: ProductType;
  registrationSubType: string;
  registrationType: string;
  serviceAgreementId: string;
  brokerageAccountNumber?: string;
  cashManagementAccount?: boolean;
  marginCode: MarginCode;
  optionPermission: OptionPermission;
  positions: Position[];
  nickname?: string;
  restrictions: RestrictionType[];
}

export enum RestrictionType {
  ACTR = 'ACTR',
}

export interface ServiceAgreement {
  serviceAgreementId: number;
  serviceId: string;
  effectiveEndDate: Date;
  grantedRoles: GrantedRole[];
  registeredRoles: RegisteredRole[];
}

export interface RegisteredRole {
  poid: number;
  roleType: string;
  permissionType: string;
  primaryOwner: boolean;
  authorizedSigner: boolean;
}
export interface GrantedRole {
  poid: string;
  roleType: string;
  primaryOwner: boolean;
  accessLevel: string;
}

export interface FilteredClientAccount {
  vgClientPoid: string | null;
  accounts: FilteredAccount[];
}

export interface FilteredAccount {
  accountId: string;
  accountName: string;
  accountNumber: string;
  accountType: AANAccountType;
  balance: string;
  balanceAsOfDateTime: string;
  cashManagementAccount: boolean;
  isBrokerage: boolean;
  managed: boolean;
  positions: Position[];
  productType: ProductType;
  registrationSubType: string;
  registrationType: string;
  serviceAgreementId: string;
  registeredRoles?: RegisteredRole[];
  grantedRoles?: GrantedRole[];
  formattedAccountName?: string;
  balanceDisplay?: string;
  dateDisplay?: string;
  nickname?: string;
  restrictions: RestrictionType[];
}

export interface RejectedAccount {
  accountId: string;
  accountName: string;
  accountNumber: string;
  accountType: AANAccountType;
  formattedAccountName: string;
  nickname?: string;
  rejectionReason?: AccountRejectionReasons;
}

export interface FilteredAccountWithMinorSTP extends FilteredAccount {
  verifiedForSTP: boolean;
}

export enum ProductType {
  MUTUAL_FUND = 'MUTUAL_FUND',
  MUTUAL_FUND_RETIREMENT = 'MUTUAL_FUND_RETIREMENT',
  BROKERAGE = 'BROKERAGE',
  CASH_PLUS = 'CASH_PLUS',
  LEGACY_BROKERAGE = 'LEGACY_BROKERAGE',
  VANGUARD_529 = 'VANGUARD_529',
  RETIREMENT_403B = 'RETIREMENT_403B',
  OUTSIDE_MANUAL_INVESTMENTS = 'OUTSIDE_MANUAL_INVESTMENTS',
  OUTSIDE_AUTOMATIC_INVESTMENTS = 'OUTSIDE_AUTOMATIC_INVESTMENTS',
  PARTICIPANT = 'PARTICIPANT',
  SMALL_MARKET_401K = 'SMALL_MARKET_401K',
}

export enum AANAccountType {
  INDIVIDUAL = 'INDIVIDUAL',
  JOINT = 'JOINT',
  JOINT_TENANTS_IN_COMMON = 'JOINT_TENANTS_IN_COMMON',
  JOINT_TENANTS_WROS = 'JOINT_TENANTS_WROS',
  TRUST = 'TRUST',
  LIVING_TRUST = 'LIVING_TRUST',
  GRANTOR_TRUST = 'GRANTOR_TRUST',
  ROLLOVER_IRA = 'ROLLOVER_IRA',
  TRADITIONAL_IRA = 'TRADITIONAL_IRA',
  SIMPLE_IRA = 'SIMPLE_IRA',
  SEP_IRA = 'SEP_IRA',
  SEP_IRA_SINGLE_PARTICIPANT = 'SEP_IRA_SINGLE_PARTICIPANT',
  SEP_IRA_MULTIPLE_PARTICIPANT = 'SEP_IRA_MULTIPLE_PARTICIPANT',
  ROTH_IRA = 'ROTH_IRA',
  INHERITED_IRA = 'INHERITED_IRA',
  INHERITED_ROTH_IRA = 'INHERITED_ROTH_IRA',
  CONVERSION_ROTH_IRA = 'CONVERSION_ROTH_IRA',
  UGMA = 'UGMA',
  UTMA = 'UTMA',
  ROLLOVER_PSP = 'ROLLOVER_PSP',
  INDIVIDUAL_401K = 'INDIVIDUAL_401K',
  INDIVIDUAL_401K_SUSPENSE = 'INDIVIDUAL_401K_SUSPENSE',
  ROTH_INDIVIDUAL_401K = 'ROTH_INDIVIDUAL_401K',
  CUSTODIAN = 'CUSTODIAN',
  CUSTODIAN_MINOR = 'CUSTODIAN_MINOR',
  GUARDIAN = 'GUARDIAN',
  ESTATE = 'ESTATE',
  EXECUTOR_ESTATE = 'EXECUTOR_ESTATE',
  OTHER = 'OTHER',
  UNDER_WILL_TRUST = 'UNDER_WILL_TRUST',
  UNKNOWN = 'UNKNOWN',
}

export enum AANAccountTypeToAccountType {
  INDIVIDUAL = 'INDV',
  JOINT = 'JOINT',
  JOINT_TENANTS_IN_COMMON = 'JOINT',
  JOINT_TENANTS_WROS = 'JOINT_TENANTS_WROS',
  TRUST = 'TRUST',
  LIVING_TRUST = 'TRUST',
  GRANTOR_TRUST = 'TRUST',
  ROLLOVER_IRA = 'IRA',
  TRADITIONAL_IRA = 'IRA',
  SIMPLE_IRA = 'IRA',
  SEP_IRA = 'IRA',
  SEP_IRA_SINGLE_PARTICIPANT = 'IRA',
  SEP_IRA_MULTIPLE_PARTICIPANT = 'IRA',
  ROTH_IRA = 'IRA',
  INHERITED_IRA = 'IRA',
  INHERITED_ROTH_IRA = 'IRA',
  CONVERSION_ROTH_IRA = 'IRA',
  UGMA = 'UGMA',
  UTMA = 'UTMA',
  ROLLOVER_PSP = 'OTHER',
  INDIVIDUAL_401K = 'OTHER',
  INDIVIDUAL_401K_SUSPENSE = 'OTHER',
  ROTH_INDIVIDUAL_401K = 'OTHER',
  CUSTODIAN = 'OTHER',
  CUSTODIAN_MINOR = 'OTHER',
  GUARDIAN = 'OTHER',
  ESTATE = 'OTHER',
  EXECUTOR_ESTATE = 'OTHER',
  OTHER = 'OTHER',
  UNDER_WILL_TRUST = 'TRUST',
  UNKNOWN = 'EMPTY',
}

export enum MarginCode {
  CASH = 'CASH',
  MARGIN = 'MARGIN',
}

export enum OptionPermission {
  NO_PAPERS = 'NO_PAPERS',
  LEVEL_1 = 'LEVEL_1',
  LEVEL_2 = 'LEVEL_2',
  LEVEL_3 = 'LEVEL_3',
  LEVEL_4 = 'LEVEL_4',
  LEVEL_7 = 'LEVEL_7',
  LEVEL_9 = 'LEVEL_9',
  LEGACY_OPTION_PERMISSION = 'LEGACY_OPTION_PERMISSION',
}

export const Eligible_Joint_Account_Types = new Set([AANAccountType.JOINT_TENANTS_WROS]);
export const Eligible_Joint_Account_Types_Pattern = /^JOINT[_A-Z0-9]*$/;
export const Eligible_Individual_Account_Types = new Set([AANAccountType.INDIVIDUAL]);
export const Eligible_Traditional_IRA_Account_Types = new Set([
  AANAccountType.TRADITIONAL_IRA,
  AANAccountType.SEP_IRA_SINGLE_PARTICIPANT,
  AANAccountType.ROLLOVER_IRA,
]);
export const Eligible_Roth_IRA_Account_Types = new Set([AANAccountType.ROTH_IRA]);
export const Eligible_Minor_Account_Types = new Set([AANAccountType.UGMA, AANAccountType.UTMA]);
export const Eligible_Trust_Account_Types = new Set([
  AANAccountType.UNDER_WILL_TRUST,
  AANAccountType.LIVING_TRUST,
  AANAccountType.GRANTOR_TRUST,
]);
export const Eligible_Estate_Account_Types = new Set([
  AANAccountType.ESTATE,
  AANAccountType.EXECUTOR_ESTATE,
]);

export const Eligible_Estate_Role_Types = new Set([RoleTypes.EXAD, RoleTypes.EXAD]);

export const Eligible_Trust_Role_Types = new Set([RoleTypes.TRTR]);

export const Eligible_Minor_Permission_Types = new Set([PermissionType.TRNS]);

export interface IClientAccountsState {
  entity: IClientAccounts | null;
  displayedAccounts: FilteredAccount[];
  loaded: boolean;
  loading: boolean;
}

export enum AccountRejectionReasons {
  MUTUAL_FUND_ONLY = 'MUTUAL_FUND_ONLY',
  MARGIN_OPTIONS_ACCOUNT = 'MARGIN_OPTIONS_ACCOUNT',
  ZERO_BALANCE = 'ZERO_BALANCE',
  MANAGED_ACCOUNT = 'MANAGED_ACCOUNT',
  CASH_PLUS_ACCOUNT = 'CASH_PLUS_ACCOUNT',
  NOT_PRIMARY_OWNER = 'NOT_PRIMARY_OWNER',
  INCORRECT_SERVICE_ACCOUNT_COUNT = 'INCORRECT_SERVICE_ACCOUNT_COUNT',
  NON_BROKERAGE_ACCOUNT = 'NON_BROKERAGE_ACCOUNT',
  NON_MONEY_MARKET_POSITION_SELECTED = 'NON_MONEY_MARKET_POSITION_SELECTED',
  NON_BROKERAGE_OR_NON_MUTUAL_FUND_ACCOUNT = 'NON_BROKERAGE_OR_NON_MUTUAL_FUND_ACCOUNT',
  ACCOUNT_TYPE = 'ACCOUNT_TYPE',
  NON_MATCHING_ROLE_TYPE = 'NON_MATCHING_ROLE_TYPE',
  NON_MATCHING_PERMISSION_TYPE = 'NON_MATCHING_PERMISSION_TYPE',
  ACTR_RESTRICTION = 'ACTR_RESTRICTION',
}
